import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {getDegrees} from "../../service/dataService";
import {getUserDegrees} from "../../service/userService";
import BasicGrid from "../grid/BasicGrid";
import {Button, Divider, toaster} from "rsuite";
import SuccessNotification from "../notifications/SuccessNotification";
import {
    EDUCATION_ADD_ERROR,
    EDUCATION_ADD_SUCCESS,
    EDUCATION_EDIT_ERROR,
    EDUCATION_EDIT_SUCCESS,
} from "../../service/constants";
import ErrorNotification from "../notifications/ErrorNotification";
import AddEducationModal from "../modals/education/AddEducationModal";
import EditEducationModal from "../modals/education/EditEducationModal";
import {parseISO} from "rsuite/cjs/utils/dateUtils";

const defaultColumns = [
    {
        key: 'institution',
        label: 'Institution',
        flexGrow: 2,
    },
    {
        key: 'degreeName',
        label: 'Degree Name',
    },
    {
        key: 'major',
        label: 'Major',
        flexGrow: 2,
    },
    {
        key: 'minor',
        label: 'Minor',
        flexGrow: 2,
    },
    {
        key: 'dateEarned',
        label: 'Date Earned',
    },
];

const Education = () => {

    // state
    const userInfo = useSelector((state) => state.user.userInfo );
    const [loading, setLoading] = useState(false);
    const [degrees, setDegrees] = useState([]);
    const [userDegrees, setUserDegrees] = useState([]);

    // add modal
    const [addModalOpen, setAddModalOpen] = useState(false);

    // edit modal
    const [selectedDegree, setSelectedDegree] = useState({});
    const [editModalOpen, setEditModalOpen] = useState(false);

    const loadUserDegrees = useCallback(() => {
        setLoading(true);
        getUserDegrees(userInfo.id).then((education) => {
            education.forEach((degree) => {
                degree.startDate = parseISO(degree.startDate);
                degree.dateEarned = parseISO(degree.dateEarned);
            });
            setUserDegrees(education);
            setLoading(false);
        });
    }, [userInfo.id]);

    const handleAddClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={EDUCATION_ADD_SUCCESS} />, { placement: 'topCenter' });
            loadUserDegrees();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={EDUCATION_ADD_ERROR} />, { placement: 'topCenter' });
        }
        setAddModalOpen(false);
    },[loadUserDegrees]);

    const handleEditClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={EDUCATION_EDIT_SUCCESS} />, { placement: 'topCenter' });
            loadUserDegrees();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={EDUCATION_EDIT_ERROR} />, { placement: 'topCenter' });
        }
        setEditModalOpen(false);
    }, [loadUserDegrees]);

    // table
    const [columnKeys] = useState(defaultColumns.map(column => column.key));
    const columns = defaultColumns.filter(column => columnKeys.some(key => key === column.key));

    const setClickedEducation = useCallback((rowData) => {
        setEditModalOpen(true);
        setSelectedDegree(rowData);
    },[]);

    useEffect(() => {
        setLoading(true);
        getDegrees().then((data) => {
                // format the data into InputPicker format for now
                setDegrees(data.map(ele => ({label: ele.degreeName, value: ele.degreeName})));
            }
        );
        loadUserDegrees();
    }, [loadUserDegrees]);

    return useMemo(() => {
        return (
            <div className="main-content">
                <h2>Education</h2>
                <p style={{padding: '0.5rem'}}>
                    Click a row to edit/delete
                </p>
                <Button appearance="primary" onClick={() => setAddModalOpen(true)}>Add Education</Button>
                <Divider />
                <BasicGrid
                    loading={loading}
                    columns={columns}
                    data={userDegrees}
                    handleClick={setClickedEducation}
                    exportTitle={`${userInfo.lastName}-${userInfo.firstName}-education`}
                />
                <AddEducationModal
                    open={addModalOpen}
                    handleClose={handleAddClose}
                    degrees={degrees}
                    userId={userInfo.id}
                />
                <EditEducationModal
                    open={editModalOpen}
                    handleClose={handleEditClose}
                    degree={selectedDegree}
                    degrees={degrees}
                    userId={userInfo.id}
                />
            </div>
        );
    }, [
        addModalOpen,
        columns,
        degrees,
        editModalOpen,
        handleAddClose,
        handleEditClose,
        loading,
        selectedDegree,
        setClickedEducation,
        userDegrees,
        userInfo.id,
        userInfo.firstName,
        userInfo.lastName,
    ]);
}

export default Education;